import {
    type ColumnMovedEvent,
    type ColumnPinnedEvent,
    type ColumnResizedEvent,
    type GridApi,
    type RowHeightParams,
    type SelectionChangedEvent,
    type SortChangedEvent,
} from "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { type IBaseEntity } from "types/apiResponse/apiResponse";

import { AgGridReact } from "commonComponents/utilities/AgGridReact/AgGridReact";
import { type IAgGridColumn } from "commonComponents/utilities/AgGridReact/AgGridReact.types";
import { mapBTColToAgCol } from "commonComponents/utilities/AgGridReact/AgGridReact.utility";
import {
    type GridColumn,
    type IGridRowData,
    type IGroupedRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";
import { GridContainerHeader } from "commonComponents/utilities/Grid/GridContainerHeader";

import "./BillListAgGrid.less";

import { getBillAgColumnImplementations } from "./billAgColumnImplementations";
import { BillAgHeader } from "./BillAgHeader";
import {
    getReadMoreExpandKey,
    type IBillGridRow,
    type IReadMoreCellRendererParams,
    READ_MORE_ITEM_CAP,
    READ_MORE_JSON_KEYS,
} from "./billAgRow.types";

/**
 * Minimal sort shape the grid emits back to BillList. Structurally compatible with the
 * Kendo `SortDescriptor` BillList's server-side handler consumes, without coupling this
 * new AG Grid component to the Kendo data-query package (restricted import).
 */
export interface IBillSortDescriptor {
    field: string;
    dir: "asc" | "desc";
}

/**
 * Scaffolding for the AG Grid rendering of the Bills List (ado-281381), gated behind the
 * `billsAgGridMigration` flag. Mirrors the ScheduleList pattern: a thin presentational
 * component over the shared `AgGridReact` wrapper that maps the existing server-driven
 * `gridState.columnsList` into AG Grid `ColDef`s and drives sort/column changes back through
 * BillList's existing server-side handlers (client-data row model — the current
 * `/api/v1/bills/grid` contract is unchanged).
 *
 * Row selection is owned here (grid ref + selected-rows state) and surfaced to the parent
 * only through `renderToolbarContent`, mirroring GridContainer's `toolbarContentConfig`.
 * Bill-specific column implementations, footer/totals, and the saved-view toolbar are layered
 * on in follow-up work.
 */
interface IBillListAgGridProps {
    data: (IGridRowData | IGroupedRowData)[];
    columnsList: GridColumn<unknown>[];
    /**
     * Per-column footer totals keyed by column jsonKey (e.g. the summed bill amount), as
     * produced by the server and stored in `gridState.footerData`. Rendered as a pinned
     * bottom row. `null`/undefined means no footer.
     */
    footerData?: { [jsonKey: string]: unknown } | null;
    /** Reuses BillList's existing server-side sort handler (SortDescriptor-compatible shape). */
    onSortChanged: (sortingData: IBillSortDescriptor[]) => void | Promise<void>;
    /**
     * The active sort (from the current/applied saved view), keyed by column id. Seeded into the
     * column defs so AG Grid shows the sort indicator and the header toggles from the right state,
     * matching the server-applied order. Empty/undefined means no active sort.
     */
    sortingData?: IBillSortDescriptor[];
    /**
     * Column-layout persistence. This component owns the AG Grid → domain translation: it maps AG
     * column resize/move/pin events into a change and emits an *updater* (matched by column `id`).
     * The updater is applied by the parent against its latest `columnsList` via a functional state
     * update, so two AG events firing before React re-renders (e.g. a drag that both moves and
     * resizes) can't clobber each other by both closing over the same stale render snapshot. No AG
     * Grid types leak to the parent.
     */
    onColumnsListChange?: (
        updater: (current: GridColumn<unknown>[]) => GridColumn<unknown>[],
    ) => void;
    hasSelectableRows: boolean;
    /**
     * Selects standalone-page vs. modal-overlay links for bill/lien-waiver columns.
     * Pass `builderInfo.flags.billsFullPageConversion`, mirroring the Kendo column factory.
     */
    useAbsoluteLink: boolean;
    /**
     * Renders the toolbar content above the grid from the currently selected rows, mirroring
     * GridContainer's `toolbarContentConfig.renderToolbarContent`. Bills does not use the
     * `CheckedActions` (selected count + Cancel) banner — its Kendo callback ignores that
     * second argument too — so it is intentionally not passed here (parity with Kendo Bills).
     */
    renderToolbarContent?: (entities: IBaseEntity[]) => React.ReactNode | void;
    /**
     * Content rendered between the toolbar header and the grid (e.g. the Bills tabs + inbox
     * dropzone bar), mirroring GridContainer's `stationaryHeader` slot so it sits directly above
     * the table rather than above the page's content header.
     */
    stationaryHeader?: React.ReactNode;
    /**
     * Renders the floating action bar for bulk actions when rows are selected, mirroring
     * GridContainer's `renderFab`. Called with a deselect callback, the selected count, and the
     * selected rows; returns null when nothing is selected.
     */
    renderFab?: (
        onDeselect: () => void,
        selectedCount: number,
        selectedEntities: IBaseEntity[],
    ) => React.ReactNode;
    /**
     * Empty-state UI shown in place of the grid when there are no rows (filtered to zero or no
     * bills). Mirrors GridContainer's `gridEmptyState`. `null` means the grid has data.
     */
    emptyState?: React.ReactNode;
    /**
     * Shows AG Grid's loading overlay while a server-side reload is in flight (sort, paging,
     * filters, view change), so a header-sort click gives immediate feedback before data updates.
     */
    loading?: boolean;
}

export const BILLS_AG_GRID_ID = "billsList";

// AG Grid's internal id for the auto-generated selection (checkbox) column. It's excluded from the
// persisted column order. Kept as a named constant so an AG Grid upgrade that renames it surfaces
// here in one place rather than silently folding the selection column into users' saved reorders.
const AG_SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";

// Quartz theme's default row height (unmodified here — see AgGridReact's `theme` default), used as
// the floor so rows without any of the multi-line columns below still get the normal row height.
const BASE_ROW_HEIGHT = 42;

// ApproversCell renders one BTStatusBadge per approver (font-sans text-sm, 14px). Badges never wrap
// internally (fixed-height pill, `whitespace-nowrap` + text-ellipsis) — only the flex-wrap container
// wraps whole badges onto new lines — so wrapped line count depends on each badge's full text width.
const APPROVER_BADGE_FONT = "14px sans-serif";
const APPROVER_BADGE_ICON_WIDTH = 20; // BuiStatusBadge's default icon size ("md")
const APPROVER_BADGE_ICON_TEXT_GAP = 4; // BuiStatusBadge's `gap-1` between icon and text
const APPROVER_BADGE_HORIZONTAL_PADDING = 16; // BuiStatusBadge's `px-2` (8px each side)
const APPROVER_BADGE_GAP = 4; // ApproversCell's `gap-1` between badges (also the row-gap when wrapped)
const APPROVER_BADGE_HEIGHT = 24; // BuiStatusBadge's `min-h-6`
const APPROVER_CELL_VERTICAL_PADDING = 8; // ApproversCell's `py-1` (4px top + 4px bottom)
// Fallback when the approvers column can't be found (e.g. hidden/not yet in the column model) —
// picked from the column's configured default width so the estimate stays reasonable.
const DEFAULT_APPROVERS_COLUMN_WIDTH = 200;

// RelatedPurchaseOrderLinksCell/RelatedOwnerInvoicesCell render one RelatedItemTag per item, stacked
// vertically with the `useMultiTagMargins` bottom margin between them (RelatedItemTag.less).
const RELATED_TAG_HEIGHT = 28; // RelatedItemTag's `padding: 6px` + its icon/text content height
const RELATED_TAG_GAP = 8; // `RelatedItemTag--multi-tag`'s margin-bottom between stacked tags

// A canvas 2D context measures text width without touching the DOM (same technique as
// CalendarList's day-cell text estimate) — needed to estimate wrapped approver-badge line count
// synchronously, so `getRowHeight` never has to render-then-measure like `autoHeight` did.
const measureCanvasContext = document.createElement("canvas").getContext("2d");

const estimateApproverBadgeWidth = (approverName: string): number => {
    const textWidth = measureCanvasContext
        ? measureCanvasContext.measureText(approverName).width
        : approverName.length * 7;
    return (
        APPROVER_BADGE_ICON_WIDTH +
        APPROVER_BADGE_ICON_TEXT_GAP +
        textWidth +
        APPROVER_BADGE_HORIZONTAL_PADDING
    );
};

// Greedily packs badges onto lines (mirroring flex-wrap's own layout algorithm) to estimate how
// many lines the approvers column needs at its current rendered width.
const estimateApproversHeight = (approverNames: string[], columnWidth: number): number => {
    if (measureCanvasContext) {
        measureCanvasContext.font = APPROVER_BADGE_FONT;
    }
    let lineCount = 1;
    let currentLineWidth = 0;
    approverNames.forEach((name) => {
        const badgeWidth = estimateApproverBadgeWidth(name);
        const widthIfAppended =
            currentLineWidth === 0
                ? badgeWidth
                : currentLineWidth + APPROVER_BADGE_GAP + badgeWidth;
        if (widthIfAppended > columnWidth && currentLineWidth > 0) {
            lineCount += 1;
            currentLineWidth = badgeWidth;
        } else {
            currentLineWidth = widthIfAppended;
        }
    });
    return (
        lineCount * APPROVER_BADGE_HEIGHT +
        (lineCount - 1) * APPROVER_BADGE_GAP +
        APPROVER_CELL_VERTICAL_PADDING
    );
};

/** Height needed to stack `itemCount` lines/tags of `itemHeight`, separated by `gap`. */
const estimateStackedHeight = (itemCount: number, itemHeight: number, gap = 0): number =>
    itemCount === 0 ? 0 : itemCount * itemHeight + (itemCount - 1) * gap;

// Roughly how many rows fit in the wrapper's viewport-relative height (BillListAgGrid.less)
// before the grid itself needs to scroll. Below this, `domLayout="normal"` would still stretch
// the grid body to that full fixed height (mirroring ProgressInvoicesGrid's approach), leaving a
// gap of empty rows between the last real row and the pinned totals footer — so short result sets
// (a filtered search, the tail page of pagination) instead render with `domLayout="autoHeight"`,
// which sizes the grid to its content and puts the footer directly under the last row.
const MAX_ROWS_BEFORE_SCROLL = 15;

export const BillListAgGrid = (props: IBillListAgGridProps) => {
    // Hold the grid API in state (set on grid-ready) rather than reading a ref during render — the
    // FAB's deselect callback is passed to renderFab in the render body, and reading gridRef.current
    // there trips react-hooks/refs. AG Grid also hands the api to each event, used directly below.
    const [gridApi, setGridApi] = useState<GridApi<IBillGridRow> | null>(null);
    const [selectedRows, setSelectedRows] = useState<IBaseEntity[]>([]);

    // Kendo parity ("read more"): tracks which (row, column) stacked-item cells are expanded past
    // READ_MORE_ITEM_CAP. Lives here (not in the cell renderer) because getRowHeight also needs it
    // to size the row for expanded content — see handleToggleCellExpanded below.
    const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());

    const handleToggleCellExpanded = useCallback(
        (jsonKey: string, rowId: number) => {
            const key = getReadMoreExpandKey(rowId, jsonKey);
            setExpandedCells((current) => {
                const next = new Set(current);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
            // getRowHeight reads expandedCells to decide the capped vs. full height for this
            // column; AG Grid doesn't know a state change outside its own props should trigger a
            // re-measure, so force it (same pattern as the approvers-column-resize handler below).
            gridApi?.resetRowHeights();
        },
        [gridApi],
    );

    const columnDefs = useMemo(() => {
        // Seed the active sort (keyed by column id) so AG Grid renders the sort indicator and the
        // header toggles from the applied direction instead of restarting at ascending.
        const sortById = new Map(
            (props.sortingData ?? []).map((s, index) => [s.field, { dir: s.dir, index }]),
        );
        return props.columnsList.map((col) => {
            const colId = col.id != null ? String(col.id) : undefined;
            const activeSort = colId != null ? sortById.get(colId) : undefined;
            // Bake this column's jsonKey into its own isCellExpanded/onToggleCellExpanded pair so
            // the read-more cell renderers (RelatedPurchaseOrderLinksCell, RelatedOwnerInvoicesCell)
            // only need to pass their row id. Threaded via cellRendererParams (not context): AG Grid
            // diffs cellRendererParams identity per column and re-renders mounted cells on a change,
            // but context changes alone do not.
            const readMoreCellRendererParams: IReadMoreCellRendererParams | undefined =
                col.jsonKey && READ_MORE_JSON_KEYS.has(col.jsonKey)
                    ? {
                          isCellExpanded: (rowId: number) =>
                              expandedCells.has(getReadMoreExpandKey(rowId, col.jsonKey)),
                          onToggleCellExpanded: (rowId: number) =>
                              handleToggleCellExpanded(col.jsonKey, rowId),
                      }
                    : undefined;
            return {
                ...mapBTColToAgCol(col as unknown as IAgGridColumn),
                // AG Grid reports colId in resize/move/pin events. Bills' column `id` (e.g. "6")
                // differs from `jsonKey` (e.g. "jobName"), and mapBTColToAgCol leaves colId
                // defaulting to field=jsonKey — so pin colId to the integer id here to match
                // columnsList entries by id. (Not in mapBTColToAgCol; other grids rely on
                // colId = field.)
                colId,
                // Render persisted freeze state. Kendo's isFrozen is a boolean (freeze to the
                // left), so map it to AG Grid's "left" pin; the pushpin header toggles it. Use
                // `null` (not `undefined`) when unfrozen: on a columnDefs update AG Grid treats
                // `undefined` as "leave the current pin as-is", so switching to a view without
                // this column pinned would leave it stuck pinned. `null` unpins explicitly.
                pinned: col.isFrozen ? ("left" as const) : null,
                // Use initial* (uncontrolled) so AG Grid shows the applied view's sort but still
                // owns sort changes from header clicks. Using controlled `sort` here fights the
                // click: each re-render re-seeds the old sort and the user's click never sticks.
                initialSort: activeSort?.dir,
                initialSortIndex: activeSort?.index,
                cellRendererParams: readMoreCellRendererParams,
            };
        });
    }, [props.columnsList, props.sortingData, expandedCells, handleToggleCellExpanded]);

    const columnTypes = useMemo(
        () => getBillAgColumnImplementations(props.useAbsoluteLink),
        [props.useAbsoluteLink],
    );

    // Computes each row's height synchronously from `params.data`, replacing the `autoHeight: true`
    // column flag these columns used to carry. `autoHeight` makes AG Grid render a row at the
    // default height, then measure the real DOM, then resize — a reflow that happens per row as it
    // mounts, which is highly visible under virtualization (rows mount lazily while scrolling), so
    // the grid visibly "fills in" while the user scrolls (ado-282662). Estimating the height here
    // from the row's data up front avoids that render-then-measure step entirely, matching this
    // codebase's established avoidance of mount-time DOM measurement inside virtualized grids (see
    // BTStatusBadge's truncation-tooltip comment, ado-281012).
    const getRowHeight = useCallback(
        (params: RowHeightParams<IBillGridRow>): number | undefined => {
            // The pinned totals row has no per-column stacked content; use the default height.
            if (params.node.rowPinned || !params.data) {
                return undefined;
            }
            const rowId = params.data.id;
            // Kendo parity ("read more"): capped at READ_MORE_ITEM_CAP items unless this row's
            // cell for that column has been expanded — mirrors the visible item count the
            // corresponding cell renderer (RelatedPurchaseOrderLinksCell / RelatedOwnerInvoicesCell)
            // actually renders, plus 1 line for the toggle itself when there's a hidden remainder.
            // Cost codes/Variance codes aren't included here — SelectCell renders them as a single
            // ellipsis-truncated line that always fits within BASE_ROW_HEIGHT.
            const stackedItemHeight = (
                jsonKey: string,
                itemCount: number,
                itemHeight: number,
                gap: number,
            ): number => {
                const isExpanded = expandedCells.has(getReadMoreExpandKey(rowId, jsonKey));
                const visibleCount = isExpanded
                    ? itemCount
                    : Math.min(itemCount, READ_MORE_ITEM_CAP);
                const toggleHeight = isExpanded || itemCount > visibleCount ? itemHeight + gap : 0;
                return estimateStackedHeight(visibleCount, itemHeight, gap) + toggleHeight;
            };
            const approverNames =
                params.data.approvers?.approvers?.map((a) => a.approverName) ?? [];
            const approversColumnWidth =
                params.api.getColumn("approvers")?.getActualWidth() ??
                DEFAULT_APPROVERS_COLUMN_WIDTH;
            const heights = [
                BASE_ROW_HEIGHT,
                approverNames.length > 0
                    ? estimateApproversHeight(approverNames, approversColumnWidth)
                    : 0,
                stackedItemHeight(
                    "relatedPurchaseOrders",
                    params.data.relatedPurchaseOrders?.length ?? 0,
                    RELATED_TAG_HEIGHT,
                    RELATED_TAG_GAP,
                ),
                stackedItemHeight(
                    "relatedOwnerInvoices",
                    params.data.relatedOwnerInvoices?.length ?? 0,
                    RELATED_TAG_HEIGHT,
                    RELATED_TAG_GAP,
                ),
            ];
            return Math.max(...heights);
        },
        [expandedCells],
    );

    // Bills' data is a flat page of rows (no row grouping), so `props.data.length` is exactly the
    // rendered row count. Below the scroll threshold, size the grid to its content
    // ("autoHeight") so the pinned totals footer sits directly under the last row instead of at
    // the bottom of the wrapper's fixed viewport-relative height; above it, keep "normal" so AG
    // Grid virtualizes and the wrapper's height/scrollbar (BillListAgGrid.less) takes over.
    const domLayout = props.data.length > MAX_ROWS_BEFORE_SCROLL ? "normal" : "autoHeight";

    // Render the server-provided totals as a single pinned bottom row. `footerData` is keyed by
    // column jsonKey; AG Grid exposes each footer value on the pinned row as `params.value` for the
    // matching column. Currently only BillAmountCell renders a footer (it checks
    // `params.node.rowPinned === "bottom"` and reads `params.value`); other renderers read
    // `params.data` and render nothing on the footer row. It's a totals pseudo-row, not a real grid
    // row, so we cast it to the grid's row type for AG Grid's prop.
    const pinnedBottomRowData = useMemo(
        () => (props.footerData ? [props.footerData as unknown as IBillGridRow] : undefined),
        [props.footerData],
    );

    // AG Grid emits a SortChangedEvent; translate the primary sorted column back into the
    // SortDescriptor shape BillList already sends to the server. colId defaults to the
    // column's jsonKey (mapBTColToAgCol sets `field`), which is what mapGridStateToRequest
    // uses as sortColumn.
    const handleSortChanged = (event: SortChangedEvent) => {
        const sortedColumn = event.columns?.find((c) => c.getSort() != null);
        const sortingData: IBillSortDescriptor[] = sortedColumn
            ? [{ field: sortedColumn.getColId(), dir: sortedColumn.getSort() ?? "asc" }]
            : [];
        void props.onSortChanged(sortingData);
    };

    const handleSelectionChanged = (event: SelectionChangedEvent<IBillGridRow>) => {
        setSelectedRows((event.api.getSelectedRows() as IBaseEntity[]) ?? []);
    };

    // Clear selection whenever the row data identity changes (tab switch, filter, sort, paging, view
    // reload). The grid stays mounted across these server reloads and no `getRowId` is supplied, so
    // AG Grid does not guarantee a selectionChanged event on a rowData swap — without this, the FAB
    // and toolbar (fed by `selectedRows`) could keep reporting bills no longer in the grid and drive
    // bulk Approve/Void/Send-to-Accounting against stale rows. Reset both React state and AG Grid's
    // internal selection so they can't diverge.
    useEffect(() => {
        setSelectedRows([]);
        gridApi?.deselectAll();
    }, [props.data, gridApi]);

    // Translate AG Grid column events into an updated columnsList (matched by column id, which we
    // pinned as colId above) and emit the plain domain array. AG fires resize/move continuously
    // during a drag, so act only on drag-end (`event.finished`).
    const handleColumnResized = (event: ColumnResizedEvent) => {
        if (!event.finished || !event.column) {
            return;
        }
        const colId = event.column.getColId();
        const width = event.column.getActualWidth();
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent move/pin event can't overwrite this resize.
        // getColId() is always a string; normalize col.id (which may be numeric at runtime despite
        // its string type) with String() so the match can't silently miss and drop the resize.
        props.onColumnsListChange?.((current) =>
            current.map((col) => (String(col.id) === colId ? { ...col, width } : col)),
        );
        // getRowHeight reads the approvers column's live width to estimate wrapped badge lines;
        // AG Grid doesn't know row heights depend on that width, so it won't re-invoke the callback
        // on its own — force it to, or a resize would leave rows clipped/oversized until they
        // happen to remount.
        if (event.column.getColDef?.().field === "approvers") {
            event.api.resetRowHeights();
        }
    };

    const handleColumnMoved = (event: ColumnMovedEvent) => {
        if (!event.finished) {
            return;
        }
        const orderedIds = event.api
            .getColumnState()
            .map((col) => col.colId)
            .filter((colId) => colId !== AG_SELECTION_COLUMN_ID);
        const orderIndexById = new Map(orderedIds.map((id, index) => [id, index]));
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent resize/pin event can't overwrite this reorder.
        // Reorder by AG Grid's reported order, then append any columnsList entry AG didn't report
        // (keeping its relative order). Merging rather than rebuilding from getColumnState alone
        // ensures a reorder can never silently drop a column from the persisted view.
        props.onColumnsListChange?.((current) => {
            const reordered = current
                .map((col, originalIndex) => ({
                    col,
                    // String(col.id): getColumnState colIds are strings; guard against a numeric id.
                    rank: orderIndexById.get(String(col.id)) ?? orderedIds.length + originalIndex,
                }))
                .sort((a, b) => a.rank - b.rank)
                .map(({ col }, index) => ({ ...col, order: index }));
            // No reportable columns (e.g. empty grid) — leave state untouched.
            return reordered.length > 0 ? reordered : current;
        });
    };

    const handleColumnPinned = (event: ColumnPinnedEvent) => {
        // Bills' persisted model is a boolean (isFrozen) that renders as a left pin, so any pin
        // (left or right) is stored as frozen; a right pin therefore reloads as a left pin.
        const changed = new Map(
            (event.columns ?? []).map((c) => [c.getColId(), c.getPinned() != null]),
        );
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent resize/move event can't overwrite this pin change.
        // Only emit if a frozen state actually changed, so programmatic/initial pin events don't
        // falsely flag the view as having unsaved changes.
        props.onColumnsListChange?.((current) => {
            let didChange = false;
            const updated = current.map((col) => {
                // String(col.id): getColId() returns strings; guard against a numeric id.
                const id = String(col.id);
                if (!changed.has(id) || col.isFrozen === changed.get(id)) {
                    return col;
                }
                didChange = true;
                return { ...col, isFrozen: changed.get(id)! };
            });
            return didChange ? updated : current;
        });
    };

    const toolbarContent = props.renderToolbarContent?.(selectedRows);

    const handleDeselectAll = useCallback(() => {
        gridApi?.deselectAll();
        setSelectedRows([]);
    }, [gridApi]);

    const fab = props.renderFab?.(handleDeselectAll, selectedRows.length, selectedRows);

    // Render the empty state in place of the grid when there are no rows, mirroring GridContainer.
    // While a reload is in flight, keep the grid mounted so its loading overlay shows instead.
    const isEmpty = props.emptyState != null && props.data.length === 0 && !props.loading;

    return (
        <div
            className={cn("BillListAgGridWrapper", {
                // The wrapper's fixed viewport-relative height (BillListAgGrid.less) only makes
                // sense when the grid is virtualizing ("normal"); with "autoHeight" the grid sizes
                // itself to its rows, so that fixed height would otherwise leave the same empty
                // gap above the footer that this whole switch exists to avoid.
                "BillListAgGridWrapper--autoHeight": domLayout === "autoHeight",
            })}
        >
            {toolbarContent && <GridContainerHeader content={toolbarContent} autoHeight />}
            {props.stationaryHeader}
            {isEmpty ? (
                props.emptyState
            ) : (
                <AgGridReact<IBillGridRow>
                    gridId={BILLS_AG_GRID_ID}
                    onGridReady={(event) => setGridApi(event.api)}
                    // "normal" (default) keeps AG Grid virtualized within the wrapper's fixed
                    // viewport-relative height once there are enough rows to need it; below that,
                    // "autoHeight" sizes the grid to its content so the pinned totals footer sits
                    // directly under the last row instead of at the bottom of that fixed height
                    // (see MAX_ROWS_BEFORE_SCROLL above).
                    domLayout={domLayout}
                    getRowHeight={getRowHeight}
                    columnDefs={columnDefs}
                    columnTypes={columnTypes}
                    data={props.data as unknown as IBillGridRow[]}
                    loading={props.loading}
                    pinnedBottomRowData={pinnedBottomRowData}
                    defaultColDef={{
                        sortable: true,
                        editable: false,
                        resizable: true,
                        // Custom header (BillAgHeader): click the label to sort, click the pushpin
                        // to freeze. Suppress AG Grid's default three-dot menu so the pushpin is the
                        // only pin affordance (Kendo parity). onColumnPinned persists the freeze.
                        headerComponent: BillAgHeader,
                        suppressHeaderMenuButton: true,
                        suppressHeaderContextMenu: true,
                        minWidth: 120,
                    }}
                    suppressMultiSort={true}
                    onSortChanged={handleSortChanged}
                    onColumnResized={handleColumnResized}
                    onColumnMoved={handleColumnMoved}
                    onColumnPinned={handleColumnPinned}
                    onSelectionChanged={handleSelectionChanged}
                    rowSelection={
                        props.hasSelectableRows
                            ? { mode: "multiRow", hideDisabledCheckboxes: true }
                            : undefined
                    }
                />
            )}
            {fab}
        </div>
    );
};
